'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { tx } from '@akademi/site-builder-runtime';
import { AccountField, inputStyle, mutedStyle } from './accountShared';
import {
  CERTIFICATE_ACCEPT,
  INSTALLER_FIELD_LIMITS,
  MAX_CERTIFICATE_BYTES,
  formatCover,
  type InstallerAgreementToSign,
  type InstallerDraftProblem,
  type InstallerPolicyDraft,
  type InstallerPolicyType,
} from './installer';

/**
 * The parts the installer blocks share: the word the shop uses for its installers, the agreement
 * box, one policy's fields, and a problem said in words.
 */

export type Localized = string | Record<string, string>;

/** A localized prop in the page's language: `tr`, then `tr-TR`'s base, then whatever there is. */
export function inLanguage(value: Localized | undefined, language: string | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const tag = (language ?? '').toLowerCase();
  return value[tag] || value[tag.slice(0, 2)] || tx(value);
}

/** What the shop calls its installers — "installer" unless a block is told "fitter". */
export interface InstallerWords {
  noun: string;
  Noun: string;
  nounPlural: string;
}

export function useInstallerWords(noun?: Localized, nounPlural?: Localized): InstallerWords {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const one = inLanguage(noun, i18n?.language) || t('commerce.installer.noun');
  const many = inLanguage(nounPlural, i18n?.language) || (noun ? `${one}s` : t('commerce.installer.nounPlural'));
  return { noun: one, Noun: one.charAt(0).toLocaleUpperCase(i18n?.language) + one.slice(1), nounPlural: many };
}

export function megabytes(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}

/** A problem with the form, in words. */
export function useInstallerProblemText(words: InstallerWords) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const policy = (type: InstallerPolicyType) => t(`commerce.installer.policies.${type}`);
  return (problem: InstallerDraftProblem): string => {
    switch (problem.kind) {
      case 'MISSING':
        return t('commerce.installer.application.missingFields', {
          fields: problem.fields.map((field) => t(`commerce.installer.application.fields.${field}`)).join(', '),
        });
      case 'AREAS': {
        const reading = problem.reading;
        if (reading.kind === 'EMPTY') return t('commerce.installer.problems.areasEmpty');
        if (reading.kind === 'TOO_MANY') return t('commerce.installer.problems.areasTooMany', { max: reading.max });
        return t(reading.range ? 'commerce.installer.problems.notDistrictsRange' : 'commerce.installer.problems.notDistricts', {
          wrong: reading.wrong.join(', '),
        });
      }
      case 'OTHER_REGISTRATION':
        return t('commerce.installer.problems.otherRegistration');
      case 'POLICY_MISSING':
        return t('commerce.installer.problems.policyMissing', { policy: policy(problem.type) });
      case 'POLICY_INCOMPLETE':
        return t('commerce.installer.problems.policyIncomplete', { policy: policy(problem.type) });
      case 'COVER_NOT_A_NUMBER':
        return t('commerce.installer.problems.coverNotANumber', { policy: policy(problem.type) });
      case 'COVER_BELOW_MINIMUM':
        return t('commerce.installer.problems.coverBelowMinimum', {
          policy: policy(problem.type),
          amount: formatCover(problem.minimum, i18n?.language),
        });
      case 'POLICY_EXPIRED':
        return t('commerce.installer.problems.policyExpired', { policy: policy(problem.type) });
      case 'CERTIFICATE_MISSING':
        return t('commerce.installer.problems.certificateMissing', { policy: policy(problem.type) });
      case 'CERTIFICATE_TOO_LARGE':
        return t('commerce.installer.problems.certificateTooLarge', {
          policy: policy(problem.type),
          size: megabytes(MAX_CERTIFICATE_BYTES),
        });
      case 'NO_AGREEMENT':
        return t('commerce.installer.application.noAgreement', { ...words });
    }
  };
}

/** The published agreement, to read in full, with the full name typed and the box ticked beside its words. */
export function AgreementBox({
  idPrefix,
  agreement,
  fullName,
  agreed,
  onFullName,
  onAgreed,
}: {
  idPrefix: string;
  agreement: InstallerAgreementToSign;
  fullName: string;
  agreed: boolean;
  onFullName: (value: string) => void;
  onAgreed: (value: boolean) => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{agreement.title}</h3>
        <p style={{ ...mutedStyle, fontSize: 13 }}>
          {t('commerce.installer.agreement.version', { version: agreement.version })}
        </p>
      </div>
      <div
        id={`${idPrefix}-agreement-text`}
        tabIndex={0}
        aria-label={agreement.title}
        style={agreementTextStyle}
      >
        {agreement.body}
      </div>
      <AccountField
        id={`${idPrefix}-full-name`}
        label={t('commerce.installer.agreement.fullName')}
        value={fullName}
        onChange={onFullName}
        autoComplete="name"
        required
        maxLength={INSTALLER_FIELD_LIMITS.typedName}
      />
      <label style={checkRowStyle}>
        <input type="checkbox" checked={agreed} onChange={(event) => onAgreed(event.target.checked)} />
        <span>{agreement.statement || t('commerce.installer.agreement.statement')}</span>
      </label>
    </div>
  );
}

/** One policy's fields and its certificate. */
export function PolicyFields({
  idPrefix,
  type,
  value,
  onChange,
  minimum,
  legend,
  withFile = true,
  children,
}: {
  idPrefix: string;
  type: InstallerPolicyType;
  value: InstallerPolicyDraft;
  onChange: (value: InstallerPolicyDraft) => void;
  minimum?: string;
  legend?: ReactNode;
  withFile?: boolean;
  children?: ReactNode;
}) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const id = (field: string) => `${idPrefix}-${type.toLowerCase()}-${field}`;
  const set = (field: Exclude<keyof InstallerPolicyDraft, 'file'>) => (text: string) =>
    onChange({ ...value, [field]: text });
  return (
    <fieldset style={boxStyle}>
      {legend !== undefined && <legend style={labelStyle}>{legend}</legend>}
      {minimum && (
        <p style={{ ...mutedStyle, fontSize: 14 }}>
          {t('commerce.installer.policy.minimum', { amount: formatCover(minimum, i18n?.language) })}
        </p>
      )}
      <AccountField
        id={id('insurer')}
        label={t('commerce.installer.policy.insurer')}
        value={value.insurer}
        onChange={set('insurer')}
        maxLength={INSTALLER_FIELD_LIMITS.insurer}
      />
      <div style={pairStyle}>
        <AccountField
          id={id('number')}
          label={t('commerce.installer.policy.policyNumber')}
          value={value.policyNumber}
          onChange={set('policyNumber')}
          maxLength={INSTALLER_FIELD_LIMITS.policyNumber}
        />
        <AccountField
          id={id('cover')}
          label={t('commerce.installer.policy.coverAmount')}
          value={value.coverAmount}
          onChange={set('coverAmount')}
          maxLength={30}
        />
      </div>
      <AccountField
        id={id('expires')}
        label={t('commerce.installer.policy.expiresOn')}
        value={value.expiresOn}
        onChange={set('expiresOn')}
        type="date"
      />
      {withFile && (
        <div style={fieldStyle}>
          <label htmlFor={id('file')} style={labelStyle}>
            {t('commerce.installer.policy.certificate', { size: megabytes(MAX_CERTIFICATE_BYTES) })}
          </label>
          <input
            id={id('file')}
            type="file"
            accept={CERTIFICATE_ACCEPT}
            onChange={(event) => onChange({ ...value, file: event.target.files?.[0] ?? null })}
          />
        </div>
      )}
      {children}
    </fieldset>
  );
}

export function InstallerSection({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <fieldset style={sectionStyle}>
      <legend style={sectionTitleStyle}>{title}</legend>
      {note && <p style={{ ...mutedStyle, fontSize: 14 }}>{note}</p>}
      {children}
    </fieldset>
  );
}

export function InstallerTextArea({
  id,
  label,
  value,
  onChange,
  rows,
  maxLength,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
  maxLength?: number;
  hint?: string;
}) {
  return (
    <div style={fieldStyle}>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        maxLength={maxLength}
        aria-describedby={hint ? `${id}-hint` : undefined}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
      {hint && (
        <span id={`${id}-hint`} style={{ ...mutedStyle, fontSize: 13 }}>
          {hint}
        </span>
      )}
    </div>
  );
}

/** A date as the reader reads one, or the ISO day when the browser cannot format it. */
export function readableDate(value: string | null | undefined, language: string | undefined): string {
  if (!value) return '';
  try {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
    return new Intl.DateTimeFormat(language || 'en', { dateStyle: 'long' }).format(date);
  } catch {
    return value.slice(0, 10);
  }
}

export const fieldStyle: CSSProperties = { display: 'grid', gap: 4 };
export const labelStyle: CSSProperties = { fontSize: 14, fontWeight: 500 };
export const sectionTitleStyle: CSSProperties = {
  margin: '0 0 8px',
  fontSize: 'var(--font-size-lg, 18px)',
  fontWeight: 600,
};
export const sectionStyle: CSSProperties = { display: 'grid', gap: 12, margin: 0, padding: 0, border: 0, minWidth: 0 };
export const pairStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
};
export const checkRowStyle: CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15 };
export const boxStyle: CSSProperties = {
  display: 'grid',
  gap: 10,
  margin: 0,
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
  minWidth: 0,
};
export const panelStyle: CSSProperties = {
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  background: 'var(--color-surface-muted, #f3f4f6)',
};
const agreementTextStyle: CSSProperties = {
  maxHeight: 320,
  overflowY: 'auto',
  whiteSpace: 'pre-wrap',
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface, #fff)',
  fontSize: 14,
  lineHeight: 1.5,
};
